import { useRef, useState } from "react";
import { Phone, CalendarClock, Play, Pause, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, HERO_VIDEO, HERO_POSTER } from "@/data/content";

const CHIPS = ["24/7 Notrufzentrale", "Einfache Bedienung", "Wasserdichter Sender", "Beratung am Telefon"];

export default function Hero() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_POSTER}
          preload="metadata"
          aria-hidden="true"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(18,41,74,0.90) 0%, rgba(18,41,74,0.74) 45%, rgba(18,41,74,0.45) 100%)" }} />
      </div>

      <div className="relative z-10 container-x py-16 sm:py-24 lg:py-28">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-green)]/95 text-white px-4 py-1.5 text-xs sm:text-sm font-bold tracking-wide">
            SENIOREN SOS SYSTEM FÜR ZUHAUSE
          </span>
          <h1 className="mt-5 font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            SICHERHEIT<br />AUF KNOPFDRUCK
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-white/90 leading-relaxed max-w-xl">
            Ein Knopfdruck kann im Notfall entscheidend sein. Unser Hausnotruf sorgt dafür, dass Hilfe schnell dort ankommt, wo sie gebraucht wird – 24 Stunden am Tag.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button asChild className="h-14 px-7 rounded-xl bg-[color:var(--brand-green)] hover:bg-[color:var(--brand-green-alt)] text-white text-base font-bold card-shadow">
              <a href={`tel:${COMPANY.phoneHref}`} data-testid="hero-call-button">
                <Phone className="h-5 w-5 mr-1" /> Jetzt anrufen: {COMPANY.phoneDisplay}
              </a>
            </Button>
            <Button asChild variant="outline" className="h-14 px-7 rounded-xl bg-white/10 border-2 border-white text-white hover:bg-white hover:text-[color:var(--brand-navy)] text-base font-bold">
              <a href="#kontakt" data-testid="hero-callback-button">
                <CalendarClock className="h-5 w-5 mr-1" /> Rückruf anfordern
              </a>
            </Button>
          </div>

          <p className="mt-4 text-sm text-white/80 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-green)]" />
            Kostenlose Erstberatung. Keine Verpflichtung.
          </p>

          <ul className="mt-8 flex flex-wrap gap-2.5" aria-label="Vorteile auf einen Blick">
            {CHIPS.map((c) => (
              <li key={c} className="inline-flex items-center gap-2 rounded-full bg-white/12 border border-white/25 text-white px-3.5 py-1.5 text-sm font-medium backdrop-blur">
                <CheckCircle2 className="h-4 w-4 text-[color:var(--brand-green)]" /> {c}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <button
        onClick={toggle}
        data-testid="hero-video-toggle"
        className="absolute z-10 bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-black/45 hover:bg-black/65 text-white px-4 py-2 text-sm font-medium border border-white/25 transition-colors"
        aria-label={playing ? "Video pausieren" : "Video abspielen"}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        {playing ? "Video pausieren" : "Video abspielen"}
      </button>
    </section>
  );
}
